import React, { useState, useEffect, useCallback } from 'react';
import { attendanceAPI, holidayAPI, officeLocationAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { DatePicker } from '../../components/ui/date-picker';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  ChevronLeft,
  ChevronRight,
  Users,
  UserCheck,
  UserX,
  Clock,
  Home,
  Calendar,
  FileText,
  Upload,
  Plus,
  Trash2,
  Edit,
  AlertCircle,
  MapPin,
  Settings,
  Navigation,
  ShieldAlert,
} from 'lucide-react';

const AdminAttendance = () => {
  const [activeTab, setActiveTab] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dailyData, setDailyData] = useState(null);
  const [matrixData, setMatrixData] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isHolidayModalOpen, setIsHolidayModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedCellDate, setSelectedCellDate] = useState(null);
  
  const [editData, setEditData] = useState({
    status: 'present',
    check_in_time: '',
    check_out_time: '',
    is_wfh: false,
    admin_note: '',
  });
  
  const [holidayData, setHolidayData] = useState({
    date: '',
    name: '',
  });
  
  const [saving, setSaving] = useState(false);

  // Office Locations state
  const [isGeoModalOpen, setIsGeoModalOpen] = useState(false);
  const [officeLocations, setOfficeLocations] = useState([]);
  const [newOffice, setNewOffice] = useState({ name: '', maps_link: '', latitude: '', longitude: '', radius_meters: 500, company: 'both' });

  const fetchOfficeLocations = async () => {
    try { const r = await officeLocationAPI.getAll(); setOfficeLocations(r.data); } catch {}
  };

  const addOfficeLocation = async () => {
    if (!newOffice.name) {
      alert('Please enter a location name'); return;
    }
    if (!newOffice.maps_link && (!newOffice.latitude || !newOffice.longitude)) {
      alert('Please paste a Google Maps link or enter coordinates'); return;
    }
    try {
      const payload = {
        name: newOffice.name,
        radius_meters: parseInt(newOffice.radius_meters),
        company: newOffice.company,
      };
      if (newOffice.maps_link) {
        payload.maps_link = newOffice.maps_link;
      } else {
        payload.latitude = parseFloat(newOffice.latitude);
        payload.longitude = parseFloat(newOffice.longitude);
      }
      await officeLocationAPI.create(payload);
      setNewOffice({ name: '', maps_link: '', latitude: '', longitude: '', radius_meters: 500, company: 'both' });
      fetchOfficeLocations();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to add location'); }
  };

  const deleteOfficeLocation = async (id) => {
    if (!window.confirm('Remove this office location?')) return;
    try { await officeLocationAPI.delete(id); fetchOfficeLocations(); } catch {}
  };

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth() + 1;

  const fetchDailyData = useCallback(async () => {
    try {
      const response = await attendanceAPI.getDailyView(selectedDate);
      setDailyData(response.data);
    } catch (error) {
      console.error('Failed to fetch daily data:', error);
    }
  }, [selectedDate]);

  const fetchMatrixData = useCallback(async () => {
    try {
      const response = await attendanceAPI.getMatrix(year, month);
      setMatrixData(response.data);
    } catch (error) {
      console.error('Failed to fetch matrix data:', error);
    }
  }, [year, month]);

  const fetchHolidays = useCallback(async () => {
    try {
      const response = await holidayAPI.getHolidays(year);
      setHolidays(response.data);
    } catch (error) {
      console.error('Failed to fetch holidays:', error);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    if (activeTab === 'daily') {
      fetchDailyData();
    } else if (activeTab === 'matrix') {
      fetchMatrixData();
    } else if (activeTab === 'holidays') {
      fetchHolidays();
    }
  }, [activeTab, fetchDailyData, fetchMatrixData, fetchHolidays]);

  useEffect(() => {
    fetchDailyData();
  }, [selectedDate, fetchDailyData]);

  useEffect(() => { fetchOfficeLocations(); }, []);

  const handleDateChange = (offset) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + offset);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const handleMonthChange = (offset) => {
    setCurrentMonth(new Date(year, month - 1 + offset, 1));
  };

  const openEditModal = (employee, date = null) => {
    setSelectedEmployee(employee);
    setSelectedCellDate(date || selectedDate);
    
    // Pre-fill with existing data
    const existing = employee.status && employee.status !== 'not_marked';
    setEditData({
      status: existing ? employee.status : 'present',
      check_in_time: employee.check_in_time || '',
      check_out_time: employee.check_out_time || '',
      is_wfh: employee.is_wfh || false,
      admin_note: employee.admin_note || '',
    });
    
    setIsEditModalOpen(true);
  };

  const handleSaveAttendance = async () => {
    setSaving(true);
    try {
      await attendanceAPI.adminUpdate({
        employee_id: selectedEmployee.employee_id,
        date: selectedCellDate,
        ...editData,
      });
      
      if (activeTab === 'daily') {
        await fetchDailyData();
      } else {
        await fetchMatrixData();
      }
      
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Failed to update attendance:', error);
      alert(error.response?.data?.detail || 'Failed to update attendance');
    } finally {
      setSaving(false);
    }
  };

  const handleAddHoliday = async () => {
    setSaving(true);
    try {
      await holidayAPI.createHoliday(holidayData);
      await fetchHolidays();
      setIsHolidayModalOpen(false);
      setHolidayData({ date: '', name: '' });
    } catch (error) {
      console.error('Failed to add holiday:', error);
      alert(error.response?.data?.detail || 'Failed to add holiday');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHoliday = async (id) => {
    if (!window.confirm('Are you sure you want to delete this holiday?')) return;
    
    try {
      await holidayAPI.deleteHoliday(id);
      await fetchHolidays();
    } catch (error) {
      console.error('Failed to delete holiday:', error);
    }
  };

  const handleUploadCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const response = await holidayAPI.uploadCSV(file);
      alert(response.data.message);
      await fetchHolidays();
    } catch (error) {
      console.error('Failed to upload CSV:', error);
      alert(error.response?.data?.detail || 'Failed to upload CSV');
    }
    e.target.value = '';
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'present': return 'Present';
      case 'absent': return 'Absent';
      case 'half_day_morning': return 'Half Day (AM)';
      case 'half_day_afternoon': return 'Half Day (PM)';
      case 'on_leave': return 'On Leave';
      case 'not_marked': return 'Not Marked';
      default: return status;
    }
  };

  const getStatusBadge = (status) => {
    const classes = {
      present: 'bg-green-100 text-green-700',
      absent: 'bg-red-100 text-red-700',
      half_day_morning: 'bg-yellow-100 text-yellow-700',
      half_day_afternoon: 'bg-yellow-100 text-yellow-700',
      on_leave: 'bg-blue-100 text-blue-700',
      not_marked: 'bg-slate-100 text-slate-600',
    };
    return classes[status] || 'bg-slate-100 text-slate-600';
  };

  const getMatrixCellColor = (status) => {
    switch (status) {
      case 'present': return 'bg-green-500';
      case 'absent': return 'bg-red-500';
      case 'half_day_morning':
      case 'half_day_afternoon': return 'bg-yellow-500';
      case 'on_leave': return 'bg-blue-500';
      default: return 'bg-slate-200';
    }
  };

  const getDaysInMonth = () => {
    return new Date(year, month, 0).getDate();
  };

  if (loading && activeTab === 'holidays') {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1D35A0]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-attendance-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-[#1D35A0]">Attendance Management</h2>
          <p className="text-slate-500 text-sm mt-1">
            Track and manage employee attendance
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setIsGeoModalOpen(true)} data-testid="geo-fence-settings-btn">
          <Navigation className="w-4 h-4 mr-1.5" /> Geo-Fence Settings
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-flex">
          <TabsTrigger value="daily" data-testid="tab-daily">
            <Calendar className="w-4 h-4 mr-2" />
            Daily View
          </TabsTrigger>
          <TabsTrigger value="matrix" data-testid="tab-matrix">
            <Users className="w-4 h-4 mr-2" />
            Monthly Matrix
          </TabsTrigger>
          <TabsTrigger value="holidays" data-testid="tab-holidays">
            <FileText className="w-4 h-4 mr-2" />
            Holidays
          </TabsTrigger>
        </TabsList>

        {/* Daily View */}
        <TabsContent value="daily" className="space-y-5">
          {/* Stats Cards */}
          {dailyData && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="bg-white rounded-lg border border-slate-200 p-3 shadow-sm">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                    <UserCheck className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-[#1D35A0]">{dailyData.stats.present}</p>
                    <p className="text-[10px] text-slate-500">Present</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-3 shadow-sm">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                    <UserX className="w-4 h-4 text-red-600" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-[#1D35A0]">{dailyData.stats.absent}</p>
                    <p className="text-[10px] text-slate-500">Absent</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-3 shadow-sm">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-[#1D35A0]">{dailyData.stats.on_leave}</p>
                    <p className="text-[10px] text-slate-500">On Leave</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-3 shadow-sm">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-yellow-100 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-[#1D35A0]">{dailyData.stats.half_day}</p>
                    <p className="text-[10px] text-slate-500">Half Day</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-3 shadow-sm">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                    <AlertCircle className="w-4 h-4 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-[#1D35A0]">{dailyData.stats.not_marked}</p>
                    <p className="text-[10px] text-slate-500">Not Marked</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Date Selector */}
          <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={() => handleDateChange(-1)} data-testid="prev-day-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2">
              <DatePicker
                value={selectedDate}
                onChange={(v) => setSelectedDate(v)}
                placeholder="Pick date"
                className="w-44"
                data-testid="date-picker"
              />
              {dailyData?.is_holiday && (
                <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-sm">
                  Holiday: {dailyData.holiday_name}
                </span>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={() => handleDateChange(1)} data-testid="next-day-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Employee List */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden divide-y divide-slate-100">
            {dailyData?.employees?.map((emp) => (
              <div key={emp.employee_id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-3 sm:px-4 py-2.5 hover:bg-slate-50 transition-colors gap-1.5 sm:gap-0" data-testid={`attendance-row-${emp.employee_id}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-[#1D35A0] truncate">{emp.employee_name}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-400">
                      <span>{emp.designation}</span>
                      {emp.check_in_time && <span>In: <span className="text-slate-600">{emp.check_in_time}</span></span>}
                      {emp.check_out_time && <span>Out: <span className="text-slate-600">{emp.check_out_time}</span></span>}
                      {emp.check_in_address && emp.check_in_lat && (
                        <a href={`https://www.google.com/maps?q=${emp.check_in_lat},${emp.check_in_lng}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-0.5 text-blue-500 hover:underline">
                          <MapPin className="w-3 h-3" /><span className="truncate max-w-[120px]">{emp.check_in_address}</span>
                        </a>
                      )}
                      {emp.is_outside_geofence === true && (
                        <span className="px-1 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-medium flex items-center gap-0.5">
                          <ShieldAlert className="w-3 h-3" /> {emp.distance_from_office ? `${emp.distance_from_office}m` : 'Outside'}
                        </span>
                      )}
                      {emp.is_outside_geofence === false && emp.check_in_lat && (
                        <span className="px-1 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium">In-office</span>
                      )}
                      {emp.admin_note && <span className="text-amber-600 italic">Note: {emp.admin_note}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-0 sm:ml-3">
                  {emp.is_late && <span className="text-[10px] text-red-600 font-semibold">Late</span>}
                  {emp.is_wfh && <Home className="w-3.5 h-3.5 text-blue-500" />}
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${getStatusBadge(emp.status)}`}>
                    {getStatusLabel(emp.status)}
                  </span>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => openEditModal(emp)} data-testid={`edit-btn-${emp.employee_id}`}>
                    <Edit className="w-3.5 h-3.5 mr-1" /> Override
                  </Button>
                </div>
              </div>
            ))}
            {dailyData?.employees?.length === 0 && (
              <div className="p-8 text-center text-slate-400">
                <Users className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">No attendance records for this date</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Monthly Matrix */}
        <TabsContent value="matrix" className="space-y-5">
          {/* Month Selector */}
          <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={() => handleMonthChange(-1)} data-testid="prev-month-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-semibold text-[#1D35A0]">
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <Button variant="outline" size="sm" onClick={() => handleMonthChange(1)} data-testid="next-month-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-500"></div>
              <span className="text-slate-600">Present</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-500"></div>
              <span className="text-slate-600">Absent</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-yellow-500"></div>
              <span className="text-slate-600">Half Day</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-blue-500"></div>
              <span className="text-slate-600">On Leave</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-slate-300"></div>
              <span className="text-slate-600">Holiday</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-slate-200"></div>
              <span className="text-slate-600">Not Marked</span>
            </div>
          </div>

          {/* Matrix Table */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="attendance-matrix">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase sticky left-0 bg-slate-50 z-10 min-w-[150px]">
                      Employee
                    </th>
                    {Array.from({ length: getDaysInMonth() }, (_, i) => {
                      const day = i + 1;
                      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      const isHoliday = matrixData?.holidays?.[dateStr];
                      return (
                        <th
                          key={day}
                          className={`px-1 py-2 text-center text-xs font-medium min-w-[32px] ${
                            isHoliday ? 'bg-slate-200' : ''
                          }`}
                          title={isHoliday || ''}
                        >
                          {day}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {matrixData?.matrix?.map((emp) => (
                    <tr key={emp.employee_id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 sticky left-0 bg-white z-10 border-r border-slate-200">
                        <div>
                          <p className="font-medium text-slate-900 text-xs truncate max-w-[140px]">
                            {emp.employee_name}
                          </p>
                        </div>
                      </td>
                      {Array.from({ length: getDaysInMonth() }, (_, i) => {
                        const day = i + 1;
                        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        const attendance = emp.attendance[dateStr];
                        const isHoliday = matrixData?.holidays?.[dateStr];
                        const status = attendance?.status;
                        
                        return (
                          <td
                            key={day}
                            className={`px-1 py-2 text-center cursor-pointer ${
                              isHoliday ? 'bg-slate-200' : ''
                            }`}
                            onClick={() => {
                              if (!isHoliday) {
                                openEditModal({
                                  employee_id: emp.employee_id,
                                  employee_name: emp.employee_name,
                                  status: status || 'not_marked',
                                  check_in_time: attendance?.check_in_time,
                                  check_out_time: attendance?.check_out_time,
                                  is_wfh: attendance?.is_wfh,
                                  admin_note: attendance?.admin_note,
                                }, dateStr);
                              }
                            }}
                            data-testid={`matrix-cell-${emp.employee_id}-${day}`}
                          >
                            {isHoliday ? (
                              <div className="w-6 h-6 rounded mx-auto bg-slate-300" title={isHoliday}></div>
                            ) : (
                              <div
                                className={`w-6 h-6 rounded mx-auto ${getMatrixCellColor(status)}`}
                                title={getStatusLabel(status || 'not_marked')}
                              ></div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Holidays */}
        <TabsContent value="holidays" className="space-y-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setCurrentMonth(new Date(year - 1, 0, 1))}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="font-semibold text-[#1D35A0] px-4">{year}</span>
              <Button variant="outline" size="sm" onClick={() => setCurrentMonth(new Date(year + 1, 0, 1))}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Label className="cursor-pointer">
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors">
                  <Upload className="w-4 h-4" />
                  <span>Upload CSV</span>
                </div>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleUploadCSV}
                  className="hidden"
                  data-testid="upload-csv-input"
                />
              </Label>
              <Button
                onClick={() => setIsHolidayModalOpen(true)}
                className="bg-[#1D35A0] hover:bg-[#152A80] text-white"
                data-testid="add-holiday-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Holiday
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full" data-testid="holidays-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Holiday Name</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {holidays.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-slate-500">
                      No holidays added for {year}
                    </td>
                  </tr>
                ) : (
                  holidays.map((holiday) => (
                    <tr key={holiday.id} className="hover:bg-slate-50" data-testid={`holiday-row-${holiday.id}`}>
                      <td className="px-4 py-3 text-slate-900">
                        {new Date(holiday.date + 'T00:00:00').toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </td>
                      <td className="px-4 py-3 text-slate-900 font-medium">{holiday.name}</td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteHoliday(holiday.id)}
                          data-testid={`delete-holiday-${holiday.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="text-sm text-slate-500">
            <p><strong>CSV Format:</strong> date,name (e.g., 2026-01-26,Republic Day)</p>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Attendance Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-md" data-testid="edit-attendance-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1D35A0]">
              Update Attendance
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {selectedEmployee?.employee_name} - {selectedCellDate}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Status *</Label>
              <Select
                value={editData.status}
                onValueChange={(value) => setEditData(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger data-testid="select-edit-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="present">Present</SelectItem>
                  <SelectItem value="absent">Absent</SelectItem>
                  <SelectItem value="half_day_morning">Half Day (Morning)</SelectItem>
                  <SelectItem value="half_day_afternoon">Half Day (Afternoon)</SelectItem>
                  <SelectItem value="on_leave">On Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Check-in Time</Label>
                <Input
                  type="time"
                  value={editData.check_in_time}
                  onChange={(e) => setEditData(prev => ({ ...prev, check_in_time: e.target.value }))}
                  data-testid="input-edit-check-in"
                />
              </div>
              <div className="space-y-2">
                <Label>Check-out Time</Label>
                <Input
                  type="time"
                  value={editData.check_out_time}
                  onChange={(e) => setEditData(prev => ({ ...prev, check_out_time: e.target.value }))}
                  data-testid="input-edit-check-out"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit_is_wfh"
                checked={editData.is_wfh}
                onCheckedChange={(checked) => setEditData(prev => ({ ...prev, is_wfh: checked }))}
                data-testid="checkbox-edit-wfh"
              />
              <Label htmlFor="edit_is_wfh" className="text-sm font-normal cursor-pointer">
                <Home className="w-4 h-4 inline mr-1 text-blue-500" />
                Work From Home
              </Label>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Admin Note</Label>
                {editData.admin_note && (
                  <button
                    type="button"
                    onClick={() => setEditData(prev => ({ ...prev, admin_note: '' }))}
                    className="text-xs text-red-500 hover:text-red-700 font-medium"
                    data-testid="clear-admin-note-btn"
                  >
                    Clear Note
                  </button>
                )}
              </div>
              <Textarea
                value={editData.admin_note}
                onChange={(e) => setEditData(prev => ({ ...prev, admin_note: e.target.value }))}
                placeholder="Add a note about this change..."
                className="resize-none"
                rows={2}
                data-testid="input-admin-note"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveAttendance}
                className="bg-[#1D35A0] hover:bg-[#152A80] text-white"
                disabled={saving}
                data-testid="save-edit-btn"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Holiday Modal */}
      <Dialog open={isHolidayModalOpen} onOpenChange={setIsHolidayModalOpen}>
        <DialogContent className="max-w-md" data-testid="add-holiday-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1D35A0]">
              Add Holiday
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Add a new public holiday to the calendar
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <DatePicker
                value={holidayData.date}
                onChange={(v) => setHolidayData(prev => ({ ...prev, date: v }))}
                placeholder="Pick date"
                data-testid="input-holiday-date"
              />
            </div>

            <div className="space-y-2">
              <Label>Holiday Name *</Label>
              <Input
                value={holidayData.name}
                onChange={(e) => setHolidayData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Republic Day"
                data-testid="input-holiday-name"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsHolidayModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAddHoliday}
                className="bg-[#1D35A0] hover:bg-[#152A80] text-white"
                disabled={saving || !holidayData.date || !holidayData.name}
                data-testid="save-holiday-btn"
              >
                {saving ? 'Saving...' : 'Add Holiday'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Geo-Fence Settings Dialog */}
      <Dialog open={isGeoModalOpen} onOpenChange={setIsGeoModalOpen}>
        <DialogContent className="max-w-lg" data-testid="geo-fence-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1D35A0]">Office Geo-Fence Settings</DialogTitle>
            <DialogDescription>Configure office locations for attendance tracking. Employees marking attendance outside the radius will be flagged.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Existing locations */}
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {officeLocations.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-3">No office locations configured. Geo-fencing is disabled.</p>
              )}
              {officeLocations.map(loc => (
                <div key={loc.id} className="flex items-center justify-between px-3 py-2.5 bg-slate-50 rounded-lg" data-testid={`office-loc-${loc.id}`}>
                  <div>
                    <p className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                      <MapPin className="w-4 h-4 text-blue-500" />
                      {loc.name}
                    </p>
                    <p className="text-xs text-slate-400 ml-5.5">{loc.latitude.toFixed(5)}, {loc.longitude.toFixed(5)} &middot; {loc.radius_meters}m radius &middot; {loc.company}</p>
                  </div>
                  <button onClick={() => deleteOfficeLocation(loc.id)} className="text-slate-400 hover:text-red-500 p-1" data-testid={`delete-loc-${loc.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Add new */}
            <div className="border-t border-slate-200 pt-4 space-y-3">
              <p className="text-sm font-semibold text-[#1D35A0]">Add Office Location</p>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <Label className="text-xs">Location Name</Label>
                  <Input placeholder="e.g. Andheri Office, Bandra Co-working" value={newOffice.name} onChange={e => setNewOffice(p => ({ ...p, name: e.target.value }))} data-testid="office-name-input" />
                </div>
                <div>
                  <Label className="text-xs">Google Maps Link</Label>
                  <Input placeholder="Paste Google Maps link here..." value={newOffice.maps_link} onChange={e => setNewOffice(p => ({ ...p, maps_link: e.target.value }))} data-testid="office-maps-link-input" />
                  <p className="text-[10px] text-slate-400 mt-0.5">Open Google Maps, search your office, click Share and paste the link</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 text-center">— or enter coordinates manually —</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Latitude</Label>
                    <Input type="number" step="any" placeholder="19.1176" value={newOffice.latitude} onChange={e => setNewOffice(p => ({ ...p, latitude: e.target.value }))} data-testid="office-lat-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Longitude</Label>
                    <Input type="number" step="any" placeholder="72.8562" value={newOffice.longitude} onChange={e => setNewOffice(p => ({ ...p, longitude: e.target.value }))} data-testid="office-lng-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Radius (meters)</Label>
                    <Input type="number" min="50" step="50" value={newOffice.radius_meters} onChange={e => setNewOffice(p => ({ ...p, radius_meters: e.target.value }))} data-testid="office-radius-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Company</Label>
                    <Select value={newOffice.company} onValueChange={v => setNewOffice(p => ({ ...p, company: v }))}>
                      <SelectTrigger data-testid="office-company-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="both">Both</SelectItem>
                        <SelectItem value="empact_studio">Empact Studio</SelectItem>
                        <SelectItem value="event_resource">Event Resource</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={addOfficeLocation} className="bg-[#1D35A0] hover:bg-[#152A80] text-white" data-testid="add-office-location-btn">
                  <MapPin className="w-4 h-4 mr-1.5" /> Add Location
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAttendance;
